import { useState } from "react";
import { Modal } from "semantic-ui-react";

import { usePermanentRealTimeUser } from "hooks";
import { createFormHelper } from "lib/Form";
import "modules/DealerDocumentations/components/AddEditDealerDocumentationModal/AddEditDealerDocumentationModal.scss";
import {
  AddEditDealerDocumentationModalCustomConfirm,
  DealerDocumentationForm
} from "modules/DealerDocumentations/components/AddEditDealerDocumentationModal/components";
import { useCreateDocumentationMutation, useUpdateDocumentationMutation } from "modules/DealerDocumentations/components/AddEditDealerDocumentationModal/hooks";
import { toISOString } from "utils/dateHelperFunctions";

import type { DealerDocumentation } from "models";
import type { DealerDocumentationFormValues } from "modules/DealerDocumentations/components/AddEditDealerDocumentationModal/components";

type AddEditDealerDocumentationModalProps = {
  documentation: DealerDocumentation;
  handleSetShowAddEditDealerDocumentationModal: (value: DealerDocumentation | null) => void;
};

const FormHelper = createFormHelper<DealerDocumentationFormValues>();

export const AddEditDealerDocumentationModal = ({ documentation, handleSetShowAddEditDealerDocumentationModal }: AddEditDealerDocumentationModalProps) => {
  const { user } = usePermanentRealTimeUser();
  const { mutate: createDocumentation, isPending: isCreateDocumentationPending } = useCreateDocumentationMutation();
  const { mutate: updateDocumentation, isPending: isUpdateDocumentationPending } = useUpdateDocumentationMutation();

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const isFormInvalid = ({ title, content, publish_date, dealer_ids, dealer_location_ids }: DealerDocumentationFormValues) =>
    !title || !content || !publish_date || (!dealer_ids.length && !dealer_location_ids.length);

  const handleCloseModal = () => handleSetShowAddEditDealerDocumentationModal(null);

  const handleSubmit = (formValues: DealerDocumentationFormValues) => {
    if (isFormInvalid(formValues)) return;

    const now = new Date().toISOString();
    const payload = {
      ...formValues,
      id: documentation.id,
      created_on: now,
      updated_on: now,
      created_by_id: user?.id ?? 0,
      updated_by_id: user?.id ?? 0,
      publish_date: toISOString(formValues.publish_date),
      visible_to_all: documentation.visible_to_all
    };

    if (documentation.id) return updateDocumentation(payload, { onSuccess: handleCloseModal });
    else createDocumentation(payload, { onSuccess: handleCloseModal });
  };

  return (
    <>
      <Modal className="AddEditDealerDocumentationModal" open={true} size="large">
        <FormHelper.Form
          onSubmit={handleSubmit}
          defaultValues={{
            ...documentation,
            publish_date: documentation.publish_date ? new Date(documentation.publish_date) : null,
            dealer_ids: documentation.dealer_ids ?? [],
            dealer_location_ids: documentation.dealer_location_ids ?? []
          }}
        >
          <DealerDocumentationForm
            documentation={documentation}
            isCreateDocumentationPending={isCreateDocumentationPending}
            isUpdateDocumentationPending={isUpdateDocumentationPending}
            closeModal={handleCloseModal}
            setShowDeleteConfirmation={setShowDeleteConfirmation}
            onSubmit={handleSubmit}
          />
        </FormHelper.Form>
      </Modal>

      <AddEditDealerDocumentationModalCustomConfirm
        documentation={documentation}
        showDeleteConfirmation={showDeleteConfirmation}
        setShowDeleteConfirmation={setShowDeleteConfirmation}
        handleSetShowAddEditDealerDocumentationModal={handleSetShowAddEditDealerDocumentationModal}
      />
    </>
  );
};
